// File: frontend/src/hooks/useStixBundles.ts
import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import {
  STIXBundleEnriched,
  STIXBundlesListResponse,
  STIXStatistics,
  STIXBundle,
  BundleFilters
} from '../types/stix';
import { Report } from '../types';

export const useStixBundles = () => {
  const { authenticatedFetcher } = useApi();
  const [bundles, setBundles] = useState<STIXBundleEnriched[]>([]);
  const [filteredBundles, setFilteredBundles] = useState<STIXBundleEnriched[]>([]);
  const [statistics, setStatistics] = useState<STIXStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await authenticatedFetcher('/api/stix/statistics');
      setStatistics(stats);
    } catch (err) {
      console.error('Error fetching STIX statistics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    }
  }, [authenticatedFetcher]);

  // Fetch bundles list
  const fetchBundles = useCallback(async (limit: number = 100, offset: number = 0) => {
    try {
      setLoading(true);
      const response: STIXBundlesListResponse = await authenticatedFetcher(
        `/api/stix/bundles?limit=${limit}&offset=${offset}`
      );

      // Enrich bundles with report details
      const enrichedBundles = await Promise.all(
        response.bundles.map(async (bundle) => {
          try {
            const report: Report = await authenticatedFetcher(`/api/reports/${bundle.articleId}`);
            return {
              ...bundle,
              title: report.title || 'Untitled Report',
              articleUrl: report.articleUrl,
              publishedDate: report.publishedDate,
              sourceFeedName: report.sourceFeedName
            } as STIXBundleEnriched;
          } catch {
            return {
              ...bundle,
              title: 'Unknown Report',
              articleUrl: undefined,
              publishedDate: undefined,
              sourceFeedName: undefined
            } as STIXBundleEnriched;
          }
        })
      );

      setBundles(enrichedBundles);
      setFilteredBundles(enrichedBundles);
      setError(null);
    } catch (err) {
      console.error('Error fetching STIX bundles:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bundles');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetcher]);

  // Fetch single bundle
  const fetchBundle = useCallback(async (articleId: string): Promise<STIXBundle> => {
    try {
      const bundle = await authenticatedFetcher(`/api/stix/bundle/${articleId}`);
      return bundle;
    } catch (err) {
      console.error(`Error fetching bundle ${articleId}:`, err);
      throw err;
    }
  }, [authenticatedFetcher]);

  // Download bundle
  const downloadBundle = useCallback((articleId: string, title?: string) => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/stix/bundle/${articleId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `stix-bundle-${title || articleId}.json`;
    a.click();
  }, []);

  // Apply filters
  const applyFilters = useCallback((filters: BundleFilters) => {
    let filtered = bundles;

    // Filter by search term (title or articleId)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        (b.title?.toLowerCase().includes(searchLower)) ||
        b.articleId.toLowerCase().includes(searchLower) ||
        b.id.toLowerCase().includes(searchLower)
      );
    }

    // Filter by date range
    if (filters.dateRange.start) {
      filtered = filtered.filter(b => {
        const bundleDate = new Date(b.createdDate);
        return bundleDate >= filters.dateRange.start!;
      });
    }
    if (filters.dateRange.end) {
      filtered = filtered.filter(b => {
        const bundleDate = new Date(b.createdDate);
        return bundleDate <= filters.dateRange.end!;
      });
    }

    // Filter by object types
    if (filters.selectedObjectTypes.length > 0) {
      filtered = filtered.filter(b =>
        filters.selectedObjectTypes.every(type => (b.objectCounts[type] || 0) > 0)
      );
    }

    setFilteredBundles(filtered);
  }, [bundles]);

  // Initial load
  useEffect(() => {
    fetchStatistics();
    fetchBundles();
  }, [fetchStatistics, fetchBundles]);

  return {
    bundles: filteredBundles,
    allBundles: bundles,
    statistics,
    loading,
    error,
    fetchBundles,
    fetchBundle,
    downloadBundle,
    applyFilters,
    refreshStatistics: fetchStatistics
  };
};
